/**
 * Forms tab — shows the read-only submitted view of the questionnaire
 * attached to this appointment. Flutter parity:
 * `tab_widget/forms_tab.dart` + `patient_forms_widgets/appointment_form_widget.dart`.
 *
 * PR-1 of 2 (PROD-843) — read path only:
 *   - No form attached → placeholder
 *   - Form attached, response empty (`item: []`) → "Awaiting response"
 *   - Form attached, response has items → render the submitted view
 *
 * PR-2 will replace the "Awaiting response" branch with the editable
 * form across the 5 question types + Save flow.
 *
 * Wire shape verified against 2026-05-20 dev curl (`GET
 * /api/QuestionnaireResponses/ForPatient?appointmentId=`). The empty
 * branch returns `item: []` with `status: "InProgress"`.
 */

import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { StatusBadge } from '@scripsteam/scrips-signal-ds';
import { EmptyState, Skeleton } from '@scripsteam/scrips-signal-ds';
import {
  MAX_QUESTIONNAIRE_NESTING_DEPTH,
  getMaxNestingDepth,
} from '@/scheduling/lib/questionnaire-form-state';
import { useQuestionnaireResponseForPatient } from '@/scheduling/services/use-questionnaire-response-for-patient';
import type {
  AppointmentResponse,
  QuestionnaireSubmittedItem,
} from '@/types/scheduling-api';
import { EditableQuestionnaireForm } from './editable-questionnaire-form';
// PROD-2743 design sheet §3 (icons): DS names ON only (?ef=frameV2); OFF keeps today's lucide icon, unchanged.

interface ViewAppointmentFormsTabProps {
  appointment: AppointmentResponse | null;
}

export function ViewAppointmentFormsTab({ appointment }: ViewAppointmentFormsTabProps) {
  const questionnaireId = appointment?.questionnaireForm?.questionnaireId ?? null;
  const appointmentId = appointment?.id ?? null;

  const { data, isLoading, isError } = useQuestionnaireResponseForPatient({
    appointmentId,
    enabled: !!questionnaireId,
  });

  // (1) No form attached — Flutter doesn't render the form area at all
  // when `appointment.questionnaireForm.questionnaireId` is null.
  if (!questionnaireId) {
    return (
      <EmptyState
        icon="file"
        title="No form attached"
        description="This appointment doesn't have a questionnaire attached. Attach one when creating or editing the appointment."
      />
    );
  }

  if (isLoading) {
    return (
      <div data-slot="appt-tab-page" className="space-y-3">
        <Skeleton className="h-5 w-1/3" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-status-error">
        Couldn't load the form response.
      </div>
    );
  }

  const items = data?.item ?? [];

  // (2) Form attached but no answers yet — `item: []` per the
  // 2026-05-20 dev curl. Flutter's `appointment_form_widget.dart:185-198`
  // would render the editable form here; PR-1 stops at the placeholder
  // and PR-2 will replace it.
  if (items.length === 0) {
    // PR-2 (PROD-845): editable form. Plan A locked — backend
    // doesn't validate completeness (verified 2026-05-20 in
    // `Scrips.PracticeManagement/.../QuestionnaireResponseService.cs`)
    // so the React form properly recurses into nested Groups (fixes
    // Flutter bug at `appointment_form_widget.dart:127-130`).
    //
    // Need a patientId for the save payload's `subject` field. Prefer
    // the GET response's `subject.patientid` (the canonical subject
    // for THIS questionnaire response); fall back to the appointment's
    // patient participant if the GET response is missing the field.
    if (!appointment) return null;
    const patientId =
      data?.subject?.patientid ??
      appointment.participant?.find((p) => p.patient)?.patient?.id ??
      null;
    if (!patientId) {
      return (
        <EmptyState
          icon="file"
          title="Cannot open form"
          description="No patient is linked to this appointment. Open the appointment and confirm the patient is set."
        />
      );
    }
    return (
      <EditableQuestionnaireForm appointment={appointment} patientId={patientId} />
    );
  }

  // (3) Submitted view — read-only render of the patient's answers.
  // Hard depth check (Samer PR #262 review §5) — refuse to render if
  // a malformed backend response exceeds the supported nesting,
  // rather than silently truncating deep questions.
  const submittedMaxDepth = getMaxNestingDepth(items);
  if (submittedMaxDepth > MAX_QUESTIONNAIRE_NESTING_DEPTH) {
    return (
      <div data-slot="appt-tab-page">
        <div className="rounded-md border border-status-error bg-red-100/40 p-4 flex items-start gap-3">
          <AlertTriangle className="size-5 text-status-error shrink-0 mt-1" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-status-error">
              Response too deeply nested
            </p>
            <p className="mt-1 text-sm text-text-primary">
              This questionnaire response has Group nesting of depth{' '}
              {submittedMaxDepth}, exceeding the supported maximum of{' '}
              {MAX_QUESTIONNAIRE_NESTING_DEPTH}. The submitted answers cannot
              be displayed here. Contact support to view this response.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-slot="appt-tab-page">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 data-slot="appt-tab-title">
            {data?.questionnaireName ?? appointment?.questionnaireForm?.name ?? 'Questionnaire'}
          </h2>
          {data?.authored ? (
            <p className="mt-1 text-sm text-text-muted">
              Submitted {fmtDateTime(data.authored)}
            </p>
          ) : null}
        </div>
        {data?.status ? (
          <StatusBadge
            status={statusToBadge(data.status)}
            label={data.status}
            className="text-xs px-2 py-0.5 shrink-0"
          />
        ) : null}
      </div>

      <ul className="space-y-3">
        {items.map((item, i) => (
          <li
            key={item.id ?? item.questionId ?? `q-${i}`}
            className="rounded-xl border border-border-default bg-surface-card p-4"
          >
            <SubmittedItemRow item={item} depth={0} />
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Recursive item renderer ──────────────────────────────────────────────
//
// Note: NO silent depth-cap truncation here. The parent component runs
// `getMaxNestingDepth` against the response's `item[]` upfront and
// renders an error banner if depth exceeds
// `MAX_QUESTIONNAIRE_NESTING_DEPTH`. So this recursive renderer can
// assume depth is in bounds. See PR #262 Samer review §5 — silent
// truncation was unacceptable because it would have hidden deep
// answers from a clinician reviewing a submitted form.

function SubmittedItemRow({
  item,
  depth,
}: {
  item: QuestionnaireSubmittedItem;
  depth: number;
}) {
  const isGroup = item.type === 'Group';

  return (
    <div className={depth > 0 ? 'mt-3 pl-3 border-l-2 border-border-subtle' : ''}>
      <p className="text-sm font-semibold text-text-primary">
        {item.text ?? 'Question'}
      </p>

      {isGroup ? (
        // Group items don't carry an answer of their own — render nested
        // items recursively. Flutter does the same (the Group widget
        // contains children but no value).
        <div className="mt-1 space-y-3">
          {(item.items ?? []).map((child, i) => (
            <SubmittedItemRow
              key={child.id ?? child.questionId ?? `child-${i}`}
              item={child}
              depth={depth + 1}
            />
          ))}
        </div>
      ) : (
        <div className="mt-1 flex items-start gap-2">
          <CheckCircle2 className="size-4 mt-1 text-status-success shrink-0" />
          <div className="min-w-0">
            <p className="text-sm text-text-primary">{formatAnswer(item)}</p>
            {item.submittedOn ? (
              <p className="mt-1 text-xs text-text-muted">
                {fmtDateTime(item.submittedOn)}
              </p>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────

/**
 * Format an answer per its question type, mirroring how the Flutter
 * read-only view renders each value-type. Verbatim 5-type fan-out per
 * Flutter `appointment_form_widget.dart:147-176`.
 */
function formatAnswer(item: QuestionnaireSubmittedItem): string {
  const a = item.answer ?? null;
  switch (item.type) {
    case 'Boolean':
      if (a?.valueBoolean === true) return 'Yes';
      if (a?.valueBoolean === false) return 'No';
      return '—';
    case 'String':
      return (a?.valueString ?? '').trim() || '—';
    case 'Choice':
    case 'OpenChoice': {
      const picks = (a?.valueCoding ?? [])
        .map((c) => c?.display?.trim() ?? '')
        .filter((s) => s.length > 0);
      return picks.length > 0 ? picks.join(', ') : '—';
    }
    default:
      // Defensive — any unknown type, prefer valueString, then valueBoolean.
      if (a?.valueString) return a.valueString;
      if (a?.valueBoolean != null) return a.valueBoolean ? 'Yes' : 'No';
      return '—';
  }
}

function fmtDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function statusToBadge(status: string): 'arrived' | 'booked' | 'fulfilled' {
  const c = status.toLowerCase();
  if (c === 'completed' || c === 'fulfilled') return 'fulfilled';
  if (c === 'inprogress' || c === 'in-progress') return 'arrived';
  return 'booked';
}

